import { memo, useMemo } from 'react';
import { Box } from '@mui/material';
import NestedItem from './NestedItem';
import Groups from './Groups';

const hasActiveChild = (item: any, activeItemId: string): boolean => {
  if (item?.id === activeItemId) {
    return true;
  }

  return Boolean(
    item?.children?.some((child: any) => hasActiveChild(child, activeItemId))
  );
};

interface NavGroupProps {
  item: {
    id: string;
    url: string;
    target?: string;
    external?: boolean;
    disabled?: boolean;
    icon?: React.ElementType;
    title: string;
    level: number;
    chip?: {
      color: 'primary' | 'secondary';
      variant: 'filled' | 'outlined';
      size: 'small' | 'medium';
      label: string;
      avatar?: React.ReactNode;
    };
    children: {
      id: string;
      title: string;
      type: string;
      url: string;
      level: number;
      breadcrumbs: boolean;
      children?: {
        id: string;
        title: string;
        type: string;
        url: string;
        level: number;
        breadcrumbs: boolean;
        children?: [];
      }[];
    }[];
  };
  isChildOpen: boolean;
  isMenuOpen: (item: any) => boolean;
  activeItemId: string;
  onToggle: (id: string, items: any) => void;
}

const NavGroup = memo<NavGroupProps>(function NavGroup({
  item,
  isChildOpen,
  isMenuOpen,
  activeItemId,
  onToggle,
}) {
  const navCollapse = useMemo(
    () =>
      item.children?.map((menuItem) => {
        switch (menuItem.type) {
          case 'item':
            return (
              <NestedItem
                key={menuItem.id}
                item={menuItem}
                level={2}
                activeItemId={activeItemId}
                onToggle={onToggle}
              />
            );
          case 'collapse':
            return (
              <Groups
                item={menuItem}
                key={menuItem.id}
                level={2}
                isOpen={isMenuOpen(menuItem)}
                isActive={hasActiveChild(menuItem, activeItemId)}
                onToggle={onToggle}
              >
                {menuItem.children &&
                  menuItem.children.map((child) => (
                    <NestedItem
                      key={child.id}
                      item={child}
                      level={3}
                      activeItemId={activeItemId}
                      onToggle={onToggle}
                    />
                  ))}
              </Groups>
            );
          default:
            return null;
        }
      }),
    [item.children, isMenuOpen, activeItemId, onToggle]
  );

  return (
    <Box sx={{ mb: 1 }}>
      <Groups
        item={item}
        key={item.id}
        level={1}
        isOpen={isChildOpen}
        isActive={hasActiveChild(item, activeItemId)}
        onToggle={onToggle}
      >
        {navCollapse}
      </Groups>
    </Box>
  );
});

NavGroup.displayName = 'NavGroup';

export default NavGroup;
